import { FirestorePaginatorComponent, PageChangeEvent } from '../firestore-paginator.component'
import { merge, Observable, Subject } from 'rxjs'
import { map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'
import { FirestoreQueryConstraintsBuilder } from './firestore-query-constraints-builder'
import { FirestoreService } from '../../firestore.service'
import { FirestoreRequest } from './firestore-request'
import { FirestoreDoc } from '../../../../../../../commons/firestore/firestore-doc.model'
import { QueryConstraint, QueryDocumentSnapshot } from '@angular/fire/firestore'

export interface FirestoreListService<D extends FirestoreDoc> {
	search(request: FirestoreRequest): Observable<Array<D>>
}

abstract class AbstractFirestoreListService<D extends FirestoreDoc> implements FirestoreListService<D> {
	protected terminateSub: Subject<void> = new Subject<void>()
	protected readonly paginationService: FirestorePaginationService = new FirestorePaginationService(
		this.paginator as FirestorePaginatorComponent,
	)

	constructor(
		protected readonly firestoreService: FirestoreService<D>,
		protected readonly paginator?: FirestorePaginatorComponent,
	) {}

	search(request: FirestoreRequest): Observable<Array<D>> {
		this.terminateSub.next()
		return this.paginationService.observePagination().pipe(
			switchMap((event) =>
				this.query(this.buildQueryFn(event.builder, request)).pipe(
					tap((docs) => this.paginationService.onDocumentsChanges(docs, event.pageIndex)),
					map((docs) => docs.map((d) => ({ ...(d.data() as D), uid: d.id }) as D)),
				),
			),
			takeUntil(this.terminateSub.asObservable()),
		)
	}

	protected abstract query(queryConstraints: Array<QueryConstraint>): Observable<Array<QueryDocumentSnapshot<D>>>

	protected buildQueryFn(
		paginationBuilder: FirestoreQueryConstraintsBuilder,
		request: FirestoreRequest,
	): Array<QueryConstraint> {
		return paginationBuilder.withFilters(request.filters).withSort(request.sort).build()
	}
}

export class FirestoreCollectionService<D extends FirestoreDoc> extends AbstractFirestoreListService<D> {
	protected query(queryConstraints: Array<QueryConstraint>): Observable<Array<QueryDocumentSnapshot<D>>> {
		return this.firestoreService.readSnapshots(queryConstraints)
	}
}

export class FirestoreCollectionGroupService<D extends FirestoreDoc> extends AbstractFirestoreListService<D> {
	protected query(queryConstraints: Array<QueryConstraint>): Observable<Array<QueryDocumentSnapshot<D>>> {
		return this.firestoreService.readGroupSnapshots(queryConstraints)
	}
}

interface FirestorePaginationEvent {
	builder: FirestoreQueryConstraintsBuilder
	pageIndex: number
}

class FirestorePaginationService {
	constructor(private readonly paginator: FirestorePaginatorComponent) {}

	observePagination(): Observable<FirestorePaginationEvent> {
		this.initPagination()
		return merge(
			this.paginator.pageChange$.pipe(map((event) => this.pageEventToQueryBuilder(event))),
			this.manualPaginationSubject.asObservable().pipe(startWith(this.getFirstPageEvent())),
		)
	}

	onDocumentsChanges(docs: Array<QueryDocumentSnapshot<unknown>>, pageIndex: number): void {
		const length = docs.length

		// documents length changed, possibly removed --> go to first page
		const samePage = pageIndex === this.currentPageIndex
		const docsRemoved = this.pageLengthHistory.has(pageIndex)
			? (this.pageLengthHistory.get(pageIndex) as number) > length
			: false
		if (samePage && docsRemoved) {
			this.pageLengthHistory.clear()
			return this.loadFirstPage()
		}

		// loaded empty last page --> go back one page and mark it as last
		const overflowing = docs.length === 0 && pageIndex > 0
		if (overflowing) {
			this.pageLengthHistory.clear()
			return this.loadPreviousPageBeforeOverflowing()
		}

		this.currentPageIndex = pageIndex
		this.pageLengthHistory.set(pageIndex, length)

		if (length > 0) {
			this.first = docs[0]
			this.last = docs[length - 1]
		}

		if (this.paginator.pageSize > length) {
			this.paginator.lastPage()
		} else {
			this.paginator.markAsNotLastPage()
		}
	}

	private initPagination(): void {
		this.paginator.firstPage()
		this.first = undefined
		this.last = undefined
		this.currentPageIndex = 0
		this.pageLengthHistory.clear()
	}

	private pageEventToQueryBuilder(event: PageChangeEvent): FirestorePaginationEvent {
		const builder = new FirestoreQueryConstraintsBuilder()
		if (event.pageIndex > event.previousPageIndex) {
			builder.withStartAfter(this.last as QueryDocumentSnapshot<unknown>).withLimit(this.paginator.pageSize)
		} else {
			builder.withEndBefore(this.first as QueryDocumentSnapshot<unknown>).withLimitToLast(this.paginator.pageSize)
		}
		return { builder, pageIndex: event.pageIndex }
	}

	private getFirstPageEvent(): FirestorePaginationEvent {
		return {
			builder: new FirestoreQueryConstraintsBuilder().withLimit(this.paginator.pageSize),
			pageIndex: this.currentPageIndex,
		}
	}

	private loadFirstPage(): void {
		this.paginator.firstPage()
		this.currentPageIndex = 0
		this.manualPaginationSubject.next(this.getFirstPageEvent())
	}

	private loadPreviousPageBeforeOverflowing(): void {
		this.paginator.lastPage(true)
		this.currentPageIndex = this.currentPageIndex - 1
		const builder = new FirestoreQueryConstraintsBuilder()
			.withStartAt(this.first as QueryDocumentSnapshot<unknown>)
			.withLimit(this.paginator.pageSize)
		const beforeOverflowEvent: FirestorePaginationEvent = { builder, pageIndex: this.currentPageIndex }
		this.manualPaginationSubject.next(beforeOverflowEvent)
	}

	private first?: QueryDocumentSnapshot<unknown>
	private last?: QueryDocumentSnapshot<unknown>
	private manualPaginationSubject: Subject<FirestorePaginationEvent> = new Subject<FirestorePaginationEvent>()
	private currentPageIndex: number = 0
	/** index to length */
	private pageLengthHistory: Map<number, number> = new Map<number, number>()
}
